import { z } from 'zod';
import { TargetMetaSchema } from './element-hint';

/**
 * No-code program · Phase 10 · Natural-language step authoring.
 *
 * Powers the "What should this step do?" textarea at the top of every
 * basic-action step. The user types a single sentence
 * (`type "Hello" into the email field`) and gets back a structured
 * patch — `{ type, target, targetMeta, value }` — that the editor
 * applies to the FlowStep without any further input.
 *
 * Two paths server-side (mirrored in `packages/api/src/services/step-parse-service.ts`):
 *   1. Rule-based grammar (deterministic, no provider call) — a small
 *      regex lexicon for the basic verbs (type / click / select / hover
 *      / navigate / wait / assert). Always returns a usable response;
 *      never throws.
 *   2. LLM-augmented (opt-in via the existing intent-engine creds) —
 *      tightens the rule grammar's output for messy input. The
 *      service falls back to rule-based on any failure.
 *
 * The response intentionally re-uses the existing describe-mode
 * `TargetMetaSchema` so the chip UI in `ElementChipField` (Phase 9)
 * picks up the friendly preview without any new branch.
 *
 * The `type` field is a free string — not the `StepType` enum — so a
 * future allow-list expansion (mobile gestures, API verbs, …) doesn't
 * have to roll the contracts package. The frontend gates which step
 * types it accepts as `onParsed` patches via `NL_ENABLED_TYPES`.
 */

/**
 * Locator Strategy Overhaul · Phase 0 (2026-06-08) — minimal slice of
 * the immediately-preceding flow step. Passed to the NL parser so it
 * can disambiguate ambiguous verbs:
 *
 *   - `click "samsung under 20000"` after `type "samsung" in search`
 *     resolves to `role: 'option'` (autocomplete suggestion) rather
 *     than the default `'button'`. The runner's resolver then races
 *     `getByRole('option', { name: target })` ahead of the brittle
 *     `:has-text()` selector that long-text product titles fall back
 *     to today.
 *
 * Optional — the parser falls back to target-shape heuristics when
 * the frontend doesn't ship the context. Slim by design so frontend
 * callers don't have to serialise the full previous step blob.
 */
export const StepParseContextSchema = z.object({
  previousStepType: z.string().min(1).max(64).optional(),
  previousStepValue: z.string().max(500).optional(),
  previousStepTarget: z.string().max(500).optional(),
});
export type StepParseContext = z.infer<typeof StepParseContextSchema>;

export const StepParseRequestSchema = z.object({
  /** Free-text sentence the user typed. Trimmed by the route handler. */
  text: z.string().min(2).max(500),
  /** Authoring mode of the parent flow — informs which verbs to
   *  prioritise (`type` makes no sense in API mode, etc.). Optional;
   *  defaults to `'web'` server-side. */
  mode: z.enum(['web', 'api', 'fullstack']).optional(),
  /**
   * Locator Strategy Overhaul · Phase 0 — optional editing context.
   * Lets the parser disambiguate `click "<x>"` between button / link /
   * option / tab based on what the previous step did (notably:
   * `click` after `type` strongly implies an autocomplete option).
   */
  context: StepParseContextSchema.optional(),
  /**
   * `pending-project-knowledge` — when present the server attaches the
   * compiled project snapshot (glossary rewrite on the rule path +
   * knowledge block on the LLM path). Optional; omit = today's parse.
   */
  projectId: z.string().uuid().optional(),
});
export type StepParseRequest = z.infer<typeof StepParseRequestSchema>;

/**
 * Phase 10.x · `defer-nl-multi-step` — a single sub-step inside a
 * conjunction-split sentence. Shape mirrors the top-level single-step
 * fields (`type`, `target`, `targetMeta`, `value`, `fields`) plus a
 * confidence. The frontend applies these via a separate `onParsedSteps`
 * callback in `NaturalLanguageStepInput`, falling back to the
 * single-step path when the host doesn't wire it.
 */
export const ParsedStepSchema = z.object({
  type: z.string().min(1),
  target: z.string().optional(),
  targetMeta: TargetMetaSchema.optional(),
  value: z.string().optional(),
  fields: z.record(z.string()).optional(),
  confidence: z.number().min(0).max(1),
});
export type ParsedStep = z.infer<typeof ParsedStepSchema>;

export const StepParseResponseSchema = z.object({
  /** Inferred `StepType`. Caller validates this against its allow-list
   *  before applying the patch; an unrecognised type is treated as
   *  "no parse" rather than rewriting the step into something exotic. */
  type: z.string().min(1),
  /** Inferred element description (Phase 9 describe-mode shape). */
  target: z.string().optional(),
  /** Structured locator hints — written verbatim into `step.targetMeta`. */
  targetMeta: TargetMetaSchema.optional(),
  /** Extracted value (the quoted text in `type "X" into …`, the URL in
   *  `navigate to …`, the seconds in `wait 5 seconds`). */
  value: z.string().optional(),
  /** Phase 10.x — extra step-shape-specific fields (`step.fields`).
   *  Used by the mobile-gesture grammar so a single sentence can fill
   *  `swipe.direction` + `swipe.distance` (or `pinch.action`,
   *  `long-press.duration`) without burning the single-slot `value`.
   *  Frontend merges each entry into `step.fields` verbatim, so the
   *  keys must match the gesture step's `fieldDefs[].key` exactly. */
  fields: z.record(z.string()).optional(),
  /** 0–1. The frontend ignores patches below 0.4 so a misparse never
   *  silently rewrites a step the user is mid-authoring. */
  confidence: z.number().min(0).max(1),
  /** Adapter that produced the response: `'openai' | 'groq' | 'anthropic'`
   *  | `'rule'` (deterministic fallback). Mirrors the element-hint shape. */
  provider: z.string(),
  /** One-sentence rationale shown in the faint status row under the
   *  textarea ("Parsed by openai · 92% · type into the email field"). */
  explanation: z.string().max(280),
  /** True when the response came from the in-process cache. */
  cached: z.boolean().optional(),
  /**
   * Phase 10.x · `defer-nl-multi-step` — when the server split the
   * sentence on conjunctions (`then`, ` and `, comma) and got ≥2
   * confident sub-steps, the array surfaces here. The top-level
   * `type` / `target` / `value` still mirror clause #1 so single-step
   * clients silently land on the first step.
   *
   * Optional + `min(2)` because below 2 the field is meaningless —
   * single-step is already on the parent object.
   */
  parsedSteps: z.array(ParsedStepSchema).min(2).optional(),
  /**
   * Phase 10.x · `defer-nl-control-flow` — when the parsed step is a
   * loop / conditional opener (`loop-fixed`, `loop-foreach`,
   * `if-condition`), the server tells the frontend to splice a
   * matching closer step right after the opener (or after the last
   * splitter clause if `parsedSteps` is populated). Keeps the wire
   * one-step-per-clause; the pairing lives client-side so the
   * `insertStepsAfter` undo-group atomicity already shipped in the
   * multi-step PR is reused verbatim.
   */
  scopeEnd: z.object({ kind: z.enum(['end-loop', 'end-if']) }).optional(),
});
export type StepParseResponse = z.infer<typeof StepParseResponseSchema>;
